import React from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';

function StatCard({ icon, title, rows, accentColor, linkTo, linkIcon }) {
  const navigate = useNavigate();

  const accentMap = {
    blue: {
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-500',
      divider: 'bg-blue-100',
      link: 'text-blue-500 hover:text-blue-700',
    },
    purple: {
      iconBg: 'bg-violet-50',
      iconColor: 'text-violet-500',
      divider: 'bg-violet-100',
      link: 'text-violet-500 hover:text-violet-700',
    },
    green: {
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-500',
      divider: 'bg-emerald-100',
      link: 'text-emerald-500 hover:text-emerald-700',
    },
    orange: {
      iconBg: 'bg-orange-50',
      iconColor: 'text-orange-500',
      divider: 'bg-orange-100',
      link: 'text-orange-500 hover:text-orange-700',
    },
  };

  const colors = accentMap[accentColor] || accentMap.blue;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className={`w-8 h-8 rounded-xl ${colors.iconBg} flex items-center justify-center shrink-0`}>
            <Icon icon={icon} width="17" className={colors.iconColor} />
          </div>
          <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
        </div>
        {linkTo && (
          <button
            onClick={() => navigate(linkTo)}
            className={`${colors.link} transition-colors`}
            title="View all"
          >
            <Icon icon={linkIcon || 'lucide:arrow-up-right'} width="16" />
          </button>
        )}
      </div>

      {/* Divider */}
      <div className={`h-px w-full ${colors.divider}`} />

      {/* Rows */}
      <div className="flex flex-col gap-2.5">
        {rows.map((row, i) => (
          <div key={i} className="flex items-center justify-between">
            <span className="text-xs text-gray-500">{row.label}</span>
            <span className={`text-sm font-semibold ${row.valueColor || 'text-gray-800'}`}>
              {row.value ?? 0}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatCards({ allDashboardData }) {
  const cards = [
    {
      icon: 'ph:graduation-cap-bold',
      title: 'Trainee Stats',
      accentColor: 'blue',
      linkTo: '/admin/trainees',
      rows: [
        { label: 'Total Trainees', value: allDashboardData?.totalTrainees },
        { label: 'Inactive Trainees', value: allDashboardData?.inactiveTrainees, valueColor: 'text-rose-500' },
        { label: 'Total L&D Users', value: allDashboardData?.totalUsers },
      ],
    },
    {
      icon: 'ph:users-bold',
      title: 'User Stats',
      accentColor: 'purple',
      linkTo: '/admin/roles-and-access?tab=users',
      rows: [
        { label: 'Total Users', value: allDashboardData?.totalUsers },
        { label: 'Inactive Users', value: allDashboardData?.inactiveUsers, valueColor: 'text-orange-500' },
      ],
    },
    {
      icon: 'ph:monitor-play-bold',
      title: 'Course Stats',
      accentColor: 'green',
      linkTo: '/admin/approvalRequest',
      rows: [
        { label: 'Approved Courses', value: allDashboardData?.approvedCourses, valueColor: 'text-emerald-600' },
        { label: 'Pending Courses', value: allDashboardData?.pendingCourses, valueColor: 'text-orange-500' },
        { label: 'Live Sessions', value: allDashboardData?.activeClassrooms },
        { label: 'Total Surveys', value: allDashboardData?.totalSurveys },
        { label: 'Survey Responses', value: allDashboardData?.totalSurveyResponses, valueColor: 'text-blue-600' },
      ],
    },
    {
      icon: 'ph:headset-bold',
      title: 'Support',
      accentColor: 'orange',
      linkTo: '/admin/settings?activeTab=query',
      rows: [
        { label: 'Pending Tickets', value: allDashboardData?.pendingTickets, valueColor: 'text-orange-500' },
        { label: 'Resolved Tickets', value: allDashboardData?.resolvedTickets, valueColor: 'text-emerald-600' },
      ],
    },
  ];

  return (
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
      {cards.map((card, i) => (
        <StatCard key={i} {...card} />
      ))}
    </div>
  );
}

export default StatCards;
